import React, { useId, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';

interface FaqItem {
  question: string;
  answer: string;
}

const FaqItem: React.FC<FaqItem> = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  const contentId = useId();

  return (
    <div className="border-b border-gray-700 last:border-0">
      <button
        className="w-full flex justify-between items-center py-4 text-left focus:outline-none"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-controls={contentId}
      >
        <h3 className="text-lg font-medium text-white">{question}</h3>
        {isOpen ? (
          <ChevronUp className="text-[#5865F2] h-5 w-5 flex-shrink-0" />
        ) : (
          <ChevronDown className="text-[#5865F2] h-5 w-5 flex-shrink-0" />
        )}
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            id={contentId}
            role="region"
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <p className="text-gray-300 pb-4">{answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

interface FaqProps {
  showLink?: boolean;
  showAllQuestions?: boolean;
}

const Faq: React.FC<FaqProps> = ({ showLink = true, showAllQuestions = false }) => {
  const allFaqItems: FaqItem[] = [
    {
      question: 'Combien de temps prend une réparation informatique typique ?',
      answer: 'La plupart des réparations basiques peuvent être effectuées en 24-48 heures. Les problèmes plus complexes peuvent prendre 3-5 jours ouvrables. Nous fournissons toujours une estimation de temps avant de commencer tout travail et intervenons dans la journée pour les services à distance.'
    },
    {
      question: 'Proposez-vous des services de dépannage à domicile ?',
      answer: 'Oui, nous offrons des services à domicile pour les particuliers et les entreprises. Les frais de déplacement sont de 25€ pour un rayon jusqu\'à 30 km. Pour les interventions à distance, nous proposons un tarif à partir de 50€ avec diagnostic gratuit inclus et paiement uniquement si le problème est résolu.'
    },
    {
      question: 'Quels types de problèmes informatiques pouvez-vous résoudre ?',
      answer: 'Notre équipe d\'experts peut résoudre une large gamme de problèmes : dépannage logiciel, problèmes de réseau, configuration WiFi, gestion des emails, optimisation des performances PC, support pour applications web, récupération de données, sécurité informatique (suppression de virus) et support d\'imprimantes. Nous intervenons aussi bien pour les particuliers que pour les TPE/PME.'
    },
    {
      question: 'Offrez-vous une garantie sur vos réparations ?',
      answer: 'Toutes nos réparations sont couvertes par une garantie de 90 jours sur les pièces et la main-d\'œuvre. Nous proposons également une politique "satisfait ou remboursé" pour nos interventions à domicile et un paiement uniquement en cas de résolution pour nos services à distance.'
    },
    {
      question: 'Mon ordinateur est très lent, pouvez-vous l\'accélérer ?',
      answer: 'Oui, l\'optimisation des performances est l\'une de nos spécialités. Nous identifions les causes de la lenteur (logiciels malveillants, trop d\'applications au démarrage, disque dur saturé, mémoire insuffisante) et appliquons des solutions adaptées. Dans la plupart des cas, nous pouvons considérablement améliorer les performances de votre ordinateur sans nécessiter d\'investissement matériel supplémentaire.'
    },
    {
      question: 'Proposez-vous des services pour les TPE/PME ?',
      answer: 'Absolument. Nous offrons des services sur mesure pour les TPE/PME et indépendants : support à distance, interventions sur site, configuration réseau et postes, solutions cloud (Microsoft 365, Google Workspace), sécurité et sauvegardes. Contactez-nous pour un devis gratuit et sans engagement.'
    },
    {
      question: 'Pouvez-vous récupérer des données d\'un disque dur défaillant ?',
      answer: 'Dans la plupart des cas, oui. Notre taux de réussite pour la récupération de données est supérieur à 90% pour les disques qui n\'ont pas subi de dommages physiques. Pour les disques physiquement endommagés, nous collaborons avec des services de récupération spécialisés pour maximiser vos chances de récupérer vos données précieuses.'
    },
    {
      question: 'Quelles méthodes de paiement acceptez-vous ?',
      answer: 'Nous acceptons toutes les principales cartes de crédit, PayPal, Apple Pay, Google Pay et les espèces. Pour les clients TPE/PME, nous proposons également des modalités de paiement adaptées. De plus, nos services sont éligibles à un crédit d\'impôt de 50% pour les particuliers.'
    },
    {
      question: 'Intervenez-vous en urgence ?',
      answer: 'Oui, nous proposons des interventions le jour même selon la disponibilité de nos experts. Contactez-nous par WhatsApp pour une prise en charge rapide.'
    },
    {
      question: 'Comment puis-je prendre rendez-vous pour une intervention ?',
      answer: 'Vous pouvez facilement prendre rendez-vous en nous contactant par WhatsApp, par téléphone ou via notre formulaire de contact en ligne. Pour les problèmes urgents, nous proposons souvent des interventions le jour même. Les diagnostics rapides et les réparations mineures peuvent être effectués sans rendez-vous pour les services à distance.'
    }
  ];
  
  // Liste réduite de 6 questions pour la page d'accueil
  const shortFaqItems: FaqItem[] = allFaqItems.slice(0, 6);
  
  // Choisir quelle liste afficher
  const faqItems = showAllQuestions ? allFaqItems : shortFaqItems;

  return (
    <section id="faq" className="section-spacing bg-[#36393F]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="bg-[#2F3136] p-8 rounded-xl border border-gray-700 shadow-xl overflow-hidden max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <div className="flex items-center justify-center mb-4">
            <HelpCircle className="text-[#5865F2] h-8 w-8 mr-3" />
            <h2 className="text-2xl md:text-3xl font-bold text-white">Foire Aux <span className="text-[#5865F2]">Questions</span></h2>
          </div>
          <p className="text-gray-300 mb-8 text-center max-w-2xl mx-auto">
            Retrouvez les réponses à nos questions les plus fréquentes. Si vous ne trouvez pas ce que vous cherchez,
            n'hésitez pas à nous contacter directement.
          </p>
          
          <div className="bg-[#36393F] rounded-lg overflow-hidden border border-gray-700">
            <div className="p-6 space-y-0">
              {faqItems.map((item) => (
                <FaqItem 
                  key={item.question}
                  question={item.question} 
                  answer={item.answer} 
                />
              ))}
            </div>
          </div>
          
          <div className="mt-8 text-center">
            <p className="text-gray-300 mb-4">
              {showLink ? (
                <><RouterLink to="/depannage-informatique" className="text-[#5865F2] hover:underline">D'autres questions ?</RouterLink> Contactez-nous directement :</>
              ) : (
                <>D'autres questions ? Contactez-nous directement :</>
              )}
            </p>
            <RouterLink
              to="/contact"
              className="group relative inline-flex items-center justify-center px-6 py-3 text-base font-medium text-white transition-all duration-300 ease-in-out transform hover:scale-105 bg-gradient-to-r from-[#5865F2] via-[#4752C4] to-[#5865F2] rounded-lg shadow-lg hover:shadow-[#5865F2]/50"
            >
              <span className="relative z-10 flex items-center">
                Nous contacter
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-[#4752C4] via-[#5865F2] to-[#4752C4] rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </RouterLink>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Faq;